import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

// Premium Calendar Index - Architectural Harmony with InvoiceShow
function CalendarIndex() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const month = parseInt(searchParams.get('month') || '') || new Date().getMonth() + 1;
    const year = parseInt(searchParams.get('year') || '') || new Date().getFullYear();
    const todayStr = new Date().toISOString().split('T')[0];

    useEffect(() => {
        loadCalendar();
    }, [month, year]);

    const loadCalendar = async () => {
        try {
            setLoading(true);
            const response = await api.get('/calendar', { month, year });
            if (response.success) {
                setData(response.data);
            } else {
                setError(response.message || 'Synchronization failed for calendar registry.');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const changeMonth = (delta: number) => {
        let nextMonth = month + delta;
        let nextYear = year;
        if (nextMonth > 12) {
            nextMonth = 1;
            nextYear += 1;
        } else if (nextMonth < 1) {
            nextMonth = 12;
            nextYear -= 1;
        }
        setSearchParams({ month: nextMonth.toString(), year: nextYear.toString() });
        setSelectedDate(null);
    };

    const goToToday = () => {
        const now = new Date();
        setSearchParams({ month: String(now.getMonth() + 1), year: String(now.getFullYear()) });
        setSelectedDate(todayStr);
    };

    const filteredTransactions = selectedDate
        ? (data?.transactions || []).filter((tx: any) => tx.date && tx.date.startsWith(selectedDate))
        : (data?.transactions || []);

    if (loading && !data) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center bg-slate-50">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium text-sm">Synchronizing timeline...</div>
            </div>
        );
    }

    const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/50 min-h-screen">
            {/* Header / Actions - Styled like InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                            Financial <span className="text-indigo-600">Timeline</span>
                        </h1>
                        <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {monthName} {year}
                        </span>
                    </div>
                    <p className="text-sm text-slate-500 ml-10">
                        Operational orchestration and <span className="font-semibold text-slate-700">transaction history</span>
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={goToToday}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors shadow-sm"
                    >
                        Today
                    </button>
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                        <button
                            onClick={() => changeMonth(-1)}
                            className="p-1.5 hover:bg-slate-50 text-slate-500 rounded-lg transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <button
                            onClick={() => changeMonth(1)}
                            className="p-1.5 hover:bg-slate-50 text-slate-500 rounded-lg transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-sm font-medium">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Financial Briefing */}
                <div className="lg:col-span-2 space-y-8">
                    
                    {/* Summary Cards - Segmented Layout */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100 uppercase tracking-widest">
                        <div className="p-5 flex-1 group">
                            <h3 className="text-[10px] font-bold text-slate-400 mb-1">Inbound Value</h3>
                            <p className="text-xl font-black text-emerald-600 font-mono">₹{data?.summary.total_sales.toLocaleString()}</p>
                            <p className="text-[9px] font-bold text-slate-400 mt-1">{data?.summary.sales_count} Transactions</p>
                        </div>
                        <div className="p-5 flex-1 group">
                            <h3 className="text-[10px] font-bold text-slate-400 mb-1">Outbound Value</h3>
                            <p className="text-xl font-black text-rose-600 font-mono">₹{data?.summary.total_purchases.toLocaleString()}</p>
                            <p className="text-[9px] font-bold text-slate-400 mt-1">{data?.summary.purchases_count} Transactions</p>
                        </div>
                        <div className="p-5 flex-1 group">
                            <h3 className="text-[10px] font-bold text-slate-400 mb-1">Net Flow</h3>
                            <p className={`text-xl font-black font-mono ${((data?.summary.total_sales || 0) - (data?.summary.total_purchases || 0)) >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                                ₹{((data?.summary.total_sales || 0) - (data?.summary.total_purchases || 0)).toLocaleString()}
                            </p>
                            <p className="text-[9px] font-bold text-slate-400 mt-1">Cash Balance</p>
                        </div>
                    </div>

                    {/* Table Style Transaction Feed */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                            <h2 className="text-sm font-bold text-slate-800">
                                {selectedDate ? `Activity on ${new Date(selectedDate).toLocaleDateString(undefined, { dateStyle: 'medium' })}` : `Monthly Activity Stream`}
                            </h2>
                            {selectedDate && (
                                <button onClick={() => setSelectedDate(null)} className="text-[10px] font-black text-indigo-600 uppercase hover:underline">View All</button>
                            )}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse text-sm">
                                <thead>
                                    <tr className="bg-white border-b border-slate-100">
                                        <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">Reference</th>
                                        <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">Category</th>
                                        <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider text-right">Amount</th>
                                        <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredTransactions.map((tx: any) => (
                                        <tr key={`${tx.type}-${tx.id}`} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-900">#{tx.number}</div>
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">
                                                    {new Date(tx.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-tighter ${
                                                    tx.type === 'sale' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'
                                                }`}>
                                                    {tx.type === 'sale' ? 'Inbound' : 'Outbound'}
                                                </span>
                                            </td>
                                            <td className={`px-6 py-4 text-right font-black font-mono tracking-tighter ${tx.type === 'sale' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                ₹{tx.amount.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Link
                                                    to={tx.type === 'sale' ? `/invoices/${tx.id}` : `/purchases/${tx.id}`}
                                                    className="inline-flex p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredTransactions.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center text-slate-400 italic text-sm">
                                                No transactions found for the selected view.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Right Column - Orchestration Grid */}
                <div className="lg:col-span-1 space-y-8">
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-4">
                        <div className="grid grid-cols-7 mb-2 border-b border-slate-50">
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                                <div key={day} className="py-2 text-center text-[10px] font-black text-slate-300 uppercase">{day}</div>
                            ))}
                        </div>

                        <div className="grid grid-cols-7 gap-1">
                            {data?.calendar_days.map((day: any, idx: number) => {
                                const dayStr = day.date ? day.date.split('T')[0] : '';
                                const isSelected = selectedDate === dayStr;
                                return (
                                <button
                                    key={idx}
                                    disabled={!day.is_current_month}
                                    onClick={() => day.is_current_month && setSelectedDate(isSelected ? null : dayStr)}
                                    className={`aspect-square rounded-lg relative transition-all duration-200 flex flex-col items-center justify-center ${
                                        day.is_current_month
                                            ? `hover:bg-indigo-50 hover:text-indigo-600 ${isSelected ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200' : 'bg-white text-slate-700 border border-slate-50'}`
                                            : 'opacity-0 pointer-events-none'
                                    } ${day.is_today && !isSelected ? 'ring-2 ring-indigo-100 ring-offset-2 bg-indigo-50 text-indigo-700' : ''}`}
                                >
                                    <span className="text-xs font-bold leading-none">{new Date(day.date).getDate()}</span>
                                    <div className="absolute bottom-1.5 flex gap-0.5">
                                        {day.sales_total > 0 && <div className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-emerald-400'}`}></div>}
                                        {day.purchases_total > 0 && <div className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-rose-400'}`}></div>}
                                    </div>
                                </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Operational Perspective Matrix */}
                    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sticky top-6">
                        <h2 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-6">Execution Metrics</h2>
                        <div className="space-y-6">
                            {[
                                { label: 'Inbound Growth', val: `₹${(data?.summary.total_sales || 0).toLocaleString()}`, color: 'emerald' },
                                { label: 'Outbound Flow', val: `₹${(data?.summary.total_purchases || 0).toLocaleString()}`, color: 'rose' },
                                { label: 'Transaction Density', val: `${(data?.summary.sales_count || 0) + (data?.summary.purchases_count || 0)} Total`, color: 'slate' }
                            ].map((stat, i) => (
                                <div key={i} className="group">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-semibold text-slate-500">{stat.label}</span>
                                        <span className={`text-sm font-black font-mono tracking-tighter ${stat.color === 'emerald' ? 'text-emerald-600' : stat.color === 'rose' ? 'text-rose-600' : 'text-slate-900'}`}>{stat.val}</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full bg-${stat.color}-500/80 group-hover:bg-${stat.color}-500 transition-all duration-500`} style={{ width: '65%' }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CalendarIndex;
